/* eslint-disable react/prop-types */
import React from 'react'

import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import find from 'lodash/find'
// import { RemoveBadPitchPoints } from '../Utilities/RemoveBadPoints.ts'
import Color from '../Utilities/Colors/ColorMap.ts'
import { EVPData, Settings } from '../schema/types.ts'

/*
Current Supported Legend Types:
'callName': from gumbo, 'STRIKE', 'BALL', 'IN PLAY'
'paResult':
'pitchResult':
'pitchName':

*/

type LegendItem = {
    event: string
    eventFormatted: string
    sort: number
    pitchType?: string
    // TODO: Remove?
    isLast?: number
}

type LegendProps = {
    settings: Settings
    type: keyof EVPData
    preFormatted?: boolean
    handleMouseOver?: (e: React.MouseEvent, d: LegendItem) => void
    handleMouseOut?: (e: React.MouseEvent, d: LegendItem) => void
    handleClick?: (e: React.MouseEvent, d: LegendItem) => void
}

export default function Legend({
    settings,
    type = 'pitchName',
    preFormatted = false,
    handleMouseOver,
    handleMouseOut,
    handleClick,
}: LegendProps) {
    const legendData = preFormatted
        ? (settings.data as unknown as LegendItem[])
        : formatData(settings.data, type)

    const SCALE_SPACING = 36
    const SCALE_RADIUS = 5
    const SPLIT_COUNT = 5
    const COLUMN_LENGTH = 200
    let column = 0
    let translate = [settings.vizWidth * 1.05, 0]
    if (settings.orientation === 'portrait') {
        translate = [0, settings.vizWidth * 1.12]
        column = -COLUMN_LENGTH
    }
    let ySize = 0

    return (
        <g id="legend" transform={`translate(${translate})`}>
            {legendData
                .filter((d) =>
                    type === 'battedBallType' ? d.eventFormatted !== 'NULL' : d
                )
                .map((d, i) => {
                    ySize += 1
                    // If in portrait orientation split legend into multiple columns
                    if (
                        settings.orientation === 'portrait' &&
                        i % SPLIT_COUNT === 0
                    ) {
                        column += COLUMN_LENGTH
                        ySize = 0
                    }

                    return (
                        <g
                            key={`pitchLegend_${
                                d.eventFormatted + d.pitchType
                            }`}
                            onMouseOver={(e) =>
                                handleMouseOver && handleMouseOver(e, d)
                            }
                            onMouseOut={(e) =>
                                handleMouseOut && handleMouseOut(e, d)
                            }
                            onClick={(e) => handleClick && handleClick(e, d)}
                        >
                            <text
                                key={`legend_text_${
                                    d.eventFormatted + d.pitchType
                                }`}
                                x={column}
                                y={ySize * SCALE_SPACING}
                                className="legend"
                                style={{ fontSize: `1rem` }}
                            >
                                {d.eventFormatted}
                            </text>
                            <circle
                                key={`legend_circle_${d.pitchType}`}
                                cx={column - 14}
                                cy={ySize * SCALE_SPACING - 6}
                                r={SCALE_RADIUS / settings.vizScale}
                                stroke="#000"
                                fill={Color(type, d.event)}
                            />
                        </g>
                    )
                })}
        </g>
    )
}

function formatPitches(groupedData: Record<string, EVPData[]>) {
    return Object.entries(groupedData)
        .sort(([, valueA], [, valueB]) =>
            valueA.length > valueB.length ? -1 : 1
        )
        .map(([event]) => {
            const eventFormatted = event.toUpperCase().split('_').join(' ')
            return {
                eventFormatted,
                event,
                sort: 1,
            }
        })
}

function formatData(data: EVPData[], type: keyof EVPData): LegendItem[] {
    const types = groupBy(data, (d) => d[type])

    const whiteList = ['battedBallType', 'pitchName', 'pitchResult']

    if (whiteList.includes(type)) {
        return formatPitches(types)
    }

    if (type === 'isInZone') {
        return [
            { event: 'true', eventFormatted: 'IN STRIKE ZONE', sort: 1 },
            { event: 'false', eventFormatted: 'OUTSIDE ZONE', sort: 1 },
        ]
    }

    let nonHits: LegendItem[] = []
    let hits: LegendItem[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (let event in types) {
        if (event && event !== 'null') {
            if (event === 'grounded_into_double_play') {
                event = 'GIDP'
            }

            const eventFormatted = event.toUpperCase().split('_').join(' ')
            if (eventFormatted === 'SINGLE') {
                hits.push({
                    eventFormatted,
                    event,
                    sort: 1,
                })
            } else if (eventFormatted === 'DOUBLE') {
                hits.push({
                    eventFormatted,
                    event,
                    sort: 2,
                })
            } else if (eventFormatted === 'TRIPLE') {
                hits.push({
                    eventFormatted,
                    event,
                    sort: 3,
                })
            } else if (eventFormatted === 'HOME RUN') {
                hits.push({
                    eventFormatted,
                    event,
                    sort: 4,
                })
            } else if (eventFormatted === 'WALK') {
                nonHits.push({
                    eventFormatted: 'WALK',
                    event,
                    sort: -2,
                })
            } else if (eventFormatted === 'IN PROGRESS') {
                nonHits.push({
                    eventFormatted: 'IN PROGRESS',
                    event,
                    sort: -2,
                })
            } else if (
                eventFormatted.indexOf('SAC') > -1 &&
                !find(nonHits, (d) => d.eventFormatted === 'SACRIFICE')
            ) {
                nonHits.push({
                    eventFormatted: 'SACRIFICE',
                    event: 'sacrifice',
                    sort: 2,
                })
            } else if (
                eventFormatted.indexOf('ERROR') > -1 &&
                !find(nonHits, (d) => d.eventFormatted.indexOf('ERROR') > -1)
            ) {
                nonHits.push({
                    eventFormatted: 'ERROR',
                    event: 'error',
                    sort: 1,
                })
            } else if (
                eventFormatted.indexOf('STRIKEOUT') > -1 &&
                !find(
                    nonHits,
                    (d) => d.eventFormatted.indexOf('STRIKEOUT') > -1
                )
            ) {
                nonHits.push({
                    eventFormatted: 'STRIKEOUT',
                    event: 'strikeout',
                    sort: -1,
                })
            } else if (
                eventFormatted.indexOf('HIT BY PITCH') > -1 &&
                !find(
                    nonHits,
                    (d) => d.eventFormatted.indexOf('HIT BY PITCH') > -1
                )
            ) {
                nonHits.push({
                    eventFormatted: 'HIT BY PITCH',
                    event: 'hit_by_pitch',
                    sort: -1,
                })
            } else if (
                type === 'description' &&
                event.indexOf('hit_into_play') > -1
            ) {
                nonHits.push({
                    eventFormatted: eventFormatted.replace(
                        'HIT INTO PLAY',
                        'IN PLAY'
                    ),
                    event,
                    sort: 0,
                })
            } else if (type === 'callName' && eventFormatted === 'STRIKE') {
                nonHits.push({
                    eventFormatted: 'STRIKE',
                    event,
                    sort: 1,
                })
            } else if (type === 'callName' && eventFormatted === 'BALL') {
                nonHits.push({
                    eventFormatted: 'BALL',
                    event,
                    sort: 2,
                })
            } else if (type === 'callName' && eventFormatted === 'IN PLAY') {
                nonHits.push({
                    eventFormatted: 'IN PLAY',
                    event,
                    sort: 3,
                })
            } else if (!nonHits.find((d) => d.eventFormatted === 'FIELD OUT')) {
                nonHits.push({
                    eventFormatted: 'FIELD OUT',
                    event: 'field_out',
                    sort: 0,
                })
            }
        }
    }

    hits = sortBy(hits, 'sort')
    if (hits.length > 0) {
        hits[hits.length - 1].isLast = 1
    }
    nonHits = sortBy(nonHits, 'sort')

    return hits.concat(nonHits)
}
